import { setAuthErrorHandler, setAuthTokenProvider } from "@app/config/api/authMiddleware";
import ForbiddenErrorResponse from "@app/config/api/responses/ForbiddenErrorResponse";
import environment from "@app/config/environment";
import webSocket from "@app/config/webSocket";
import { updateSelectedTeam } from "@app/state/actions";
import { clearApplicationState } from "@app/state/cleanup";
import { useGlobalStore } from "@app/state/store";
import { useAuth0 } from "@auth0/auth0-react";
import type { UserDetails } from "@globals";
import { Intercom } from "@intercom/messenger-js-sdk";
import formatUserName from "@lib/common/utils/formatUserName";
import { getIntercomJWT } from "@lib/iam/utils/getIntercomJWT";
import { getUserDetails } from "@lib/iam/utils/getUserDetails";
import { ContextualError } from "@lib/utils/ContextualError";
import { setUser } from "@sentry/react";
import posthog from "posthog-js";
import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";

type Props = {
  children: ReactNode;
};

export interface AuthError {
  code: string;
  message: string;
}

let resolveApiSetup: (() => void) | null = null;
let isSetupComplete = false;

export const apiSetupPromise = new Promise<void>(resolve => {
  if (isSetupComplete) {
    resolve();
  } else {
    resolveApiSetup = resolve;
  }
});

export const markApiSetupComplete = () => {
  if (!isSetupComplete) {
    isSetupComplete = true;
    if (resolveApiSetup) {
      resolveApiSetup();
      resolveApiSetup = null;
    }
  }
};

export const APIAuthSetup = ({ children }: Props) => {
  const {
    user: userAuth0,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    error
  } = useAuth0();

  const asyncLogout = useCallback(
    async (reason = "") => {
      await logout({
        logoutParams: { returnTo: `${window.location.origin}/auth-error?type=${reason}`, federated: true }
      });
    },
    [logout]
  );

  const initializeSelectedTeam = async (user: UserDetails) => {
    const { selectedTeam } = useGlobalStore.getState();

    if (null === selectedTeam) {
      return;
    }

    // User had selected a team, check that they are still part of that team
    const isStillInTeam = user.teams.some(roleMapping => roleMapping.team.id === selectedTeam.id);
    if (!isStillInTeam) {
      // if not, set to personal workspace
      await updateSelectedTeam(null);
    }

    // else, keep the selected team as is
  };

  const setSentryUser = (user: UserDetails) => {
    setUser({
      id: user.id,
      email: user.email ?? undefined,
      name: formatUserName(user),
      organization_id: user.current_organization.id,
      organization_name: user.current_organization.name
    });
  };

  const setPosthogUser = (user: UserDetails) => {
    posthog.identify(user.id, {
      email: user.email,
      name: formatUserName(user),
      organization_id: user.current_organization.id,
      organization_name: user.current_organization.name ?? ""
    });

    posthog.group("organization", user.current_organization.id, {
      name: user.current_organization.name ?? ""
    });

    // posthog-js skips the implicit flag reload when identify() is called with the same
    // distinct_id already in storage (i.e. every reload of a logged-in user), so newly
    // rolled-out or restricted flags would otherwise stay stuck on the cached value until
    // logout. Force a fresh /decide call on every page load.
    posthog.reloadFeatureFlags();
  };

  const initIntercomUser = () => {
    if (!environment.ATI_PUBLIC_INTERCOM_APP_ID) {
      return;
    }

    void getIntercomJWT()
      .then(intercomJwt =>
        Intercom({
          // we check for app_id in the if statement above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          app_id: environment.ATI_PUBLIC_INTERCOM_APP_ID!,
          region: "eu",
          intercom_user_jwt: intercomJwt,
          alignment: "left" // Place Intercom messenger icon at bottom-left.
        })
      )
      .catch(intercomError => {
        // eslint-disable-next-line no-console
        console.error("Failed to initialize Intercom:", intercomError);
      });
  };

  // Handle auth errors from API calls
  const handleAuthError = useCallback(
    async (err: AuthError) => {
      // eslint-disable-next-line no-console
      console.error("Auth error occurred:", err);
      switch (err.code) {
        case "login_required":
        case "unauthorized":
          // Token expired or invalid - redirect to login
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname }
          });
          break;

        case "consent_required":
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname }
          });
          break;

        case "interaction_required":
          // User interaction required (MFA, etc.)
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname }
          });
          break;

        case "network_error":
          throw new ContextualError("Network error during authentication", { auth_error: err });

        case "timeout":
          throw new ContextualError("Authentication request timed out", { auth_error: err });

        default:
          await logout({
            logoutParams: { returnTo: window.location.origin }
          });

          break;
      }
    },
    [loginWithRedirect, logout]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const setup = async () => {
      setAuthTokenProvider(getAccessTokenSilently);
      setAuthErrorHandler(handleAuthError);

      try {
        // Check if user or organization changed by comparing Auth0 user with stored state
        const { user: storedUser } = useGlobalStore.getState();
        const currentAuth0UserId = userAuth0?.sub;
        const currentAuth0OrgId = userAuth0?.org_id as string | undefined;

        // Detect if user or organization changed
        const hasUserChanged = storedUser && currentAuth0UserId && storedUser.id !== currentAuth0UserId;
        const hasOrgChanged =
          storedUser && currentAuth0OrgId && storedUser.current_organization.id !== currentAuth0OrgId;

        if (hasUserChanged || hasOrgChanged) {
          // Clear application state before setting up new user/org
          await clearApplicationState();
        }

        const userDetails = await getUserDetails();
        setSentryUser(userDetails);
        setPosthogUser(userDetails);

        // Fetch Intercom JWT separately (don't block on this)
        initIntercomUser();

        // Initialize WebSocket in the background without blocking app load
        // WebSocket will auto-reconnect if it fails
        void webSocket.initialize(() => getAccessTokenSilently());

        await initializeSelectedTeam(userDetails);
        markApiSetupComplete();
      } catch (setupError) {
        // Handle 403 Forbidden responses gracefully to prevent app from becoming unresponsive.
        if (setupError instanceof ForbiddenErrorResponse) {
          console.error("Access denied for user details");
          // Clear all application state before logout
          await clearApplicationState();
          await asyncLogout();
          return;
        }
        // Re-throw other errors to maintain existing error handling behavior
        throw setupError;
      }
    };

    if (isAuthenticated) {
      void setup();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, handleAuthError]);

  if (error) {
    // Log the error for debug (it might not be because of the organization)
    // eslint-disable-next-line no-console
    console.log(error);
    void asyncLogout("organization");
    return null;
  }

  return children;
};
